import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  createMediaAsset,
  deleteMediaAsset,
  subscribeToMediaAssets,
  updateMediaAsset,
} from '../../services/media';
import type { MediaAsset, MediaAssetPayload, MediaAssetType } from '../../types/media';

const placementPresets = [
  {
    id: 'hero.video_demo',
    label: 'Hero · Video teaser',
  },
  {
    id: 'hero.visual_showcase',
    label: 'Hero · Visual showcase',
  },
  {
    id: 'percorsi.visual_journey',
    label: 'Percorsi · Visual journey',
  },
  {
    id: 'valueprops.emotional_hook',
    label: 'Value Props · Emotional hook',
  },
];

type FormState = {
  title: string;
  placement: string;
  type: MediaAssetType;
  description: string;
  order: string;
  mediaUrl: string;
  thumbnailUrl: string;
  file: File | null;
  thumbnailFile: File | null;
};

const defaultFormState: FormState = {
  title: '',
  placement: placementPresets[0]?.id ?? '',
  type: 'image',
  description: '',
  order: '',
  mediaUrl: '',
  thumbnailUrl: '',
  file: null,
  thumbnailFile: null,
};

export const MediaManager = () => {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingAsset, setEditingAsset] = useState<MediaAsset | null>(null);
  const [activePlacement, setActivePlacement] = useState<string>('all');

  useEffect(() => {
    const unsubscribe = subscribeToMediaAssets(
      (items) => {
        setAssets(items);
      },
      (err) => {
        setError(err.message);
      },
    );
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timeout);
    }
  }, [success]);

  const isEditing = useMemo(() => editingAsset !== null, [editingAsset]);
  const visibleAssets = useMemo(
    () => (activePlacement === 'all' ? assets : assets.filter((a) => a.placement === activePlacement)),
    [assets, activePlacement],
  );

  useEffect(() => {
    if (!isEditing && activePlacement !== 'all') {
      setFormState((prev) => ({ ...prev, placement: activePlacement }));
    }
  }, [activePlacement, isEditing]);

  const resetForm = () => {
    setFormState(defaultFormState);
    setEditingAsset(null);
    setError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formState.title.trim()) {
      setError('Inserisci un titolo.');
      return;
    }

    if (!formState.placement.trim()) {
      setError('Seleziona un placement.');
      return;
    }

    if (!formState.file && !formState.mediaUrl.trim() && !isEditing) {
      setError('Carica un file o inserisci un URL media.');
      return;
    }

    if (formState.type === 'image' && !formState.file && !formState.mediaUrl.trim()) {
      setError('Per le immagini è richiesto un file o un URL.');
      return;
    }

    try {
      setLoading(true);
      const payload: MediaAssetPayload = {
        title: formState.title.trim(),
        placement: formState.placement.trim(),
        type: formState.type,
        description: formState.description.trim() || undefined,
        order: formState.order ? Number(formState.order) : null,
        mediaUrl: formState.mediaUrl.trim() || undefined,
        thumbnailUrl: formState.thumbnailUrl.trim() || undefined,
        file: formState.file ?? undefined,
        thumbnailFile: formState.thumbnailFile ?? undefined,
      };

      if (isEditing && editingAsset) {
        await updateMediaAsset(editingAsset.id, payload, editingAsset);
        setSuccess('Asset aggiornato con successo.');
      } else {
        await createMediaAsset(payload);
        setSuccess('Asset creato con successo.');
      }

      resetForm();
    } catch (err) {
      console.error(err);
      setError('Errore durante il salvataggio dell\'asset.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (asset: MediaAsset) => {
    setEditingAsset(asset);
    setFormState({
      title: asset.title ?? '',
      placement: asset.placement ?? '',
      type: asset.type,
      description: asset.description ?? '',
      order: asset.order?.toString() ?? '',
      mediaUrl: asset.mediaUrl ?? '',
      thumbnailUrl: asset.thumbnailUrl ?? '',
      file: null,
      thumbnailFile: null,
    });
    setError(null);
    setSuccess(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (asset: MediaAsset) => {
    const confirmed = window.confirm(`Eliminare permanentemente l'asset "${asset.title}"?`);
    if (!confirmed) {
      return;
    }
    try {
      setLoading(true);
      await deleteMediaAsset(asset);
      setSuccess('Asset eliminato con successo.');
      if (editingAsset?.id === asset.id) {
        resetForm();
      }
    } catch (err) {
      console.error(err);
      setError('Errore durante l\'eliminazione dell\'asset.');
    } finally {
      setLoading(false);
    }
  };

  const onInputChange = (key: keyof FormState, value: string | File | null) => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <div className="space-y-10">
      <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-xl">
        <header className="mb-6">
          <h2 className="text-xl font-semibold text-slate-50">{isEditing ? 'Modifica asset' : 'Nuovo asset media'}</h2>
          <p className="text-sm text-slate-400">
            Carica video, immagini o collega URL esterni per popolare le sezioni marketing del sito pubblico.
          </p>
        </header>

        <form className="grid gap-5" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-200">Titolo</label>
              <input
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-50 focus:border-indigo-500 focus:outline-none"
                value={formState.title}
                onChange={(event) => onInputChange('title', event.target.value)}
                placeholder="Hero Video Experience"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-200">Placement</label>
              <select
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-50 focus:border-indigo-500 focus:outline-none"
                value={formState.placement}
                onChange={(event) => onInputChange('placement', event.target.value)}
                required
              >
                <option value="" disabled>
                  Seleziona positioning
                </option>
                {placementPresets.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-200">Tipo</label>
              <select
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-50 focus:border-indigo-500 focus:outline-none"
                value={formState.type}
                onChange={(event) => onInputChange('type', event.target.value as MediaAssetType)}
              >
                <option value="image">Immagine</option>
                <option value="video">Video</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-200">Ordine</label>
              <input
                type="number"
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-50 focus:border-indigo-500 focus:outline-none"
                value={formState.order}
                onChange={(event) => onInputChange('order', event.target.value)}
                placeholder="0"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-200">URL media</label>
              <input
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-50 focus:border-indigo-500 focus:outline-none"
                value={formState.mediaUrl}
                onChange={(event) => onInputChange('mediaUrl', event.target.value)}
                placeholder="https://cdn.regai.it/hero-demo.mp4"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-200">Descrizione</label>
              <textarea
                className="min-h-[120px] w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-50 focus:border-indigo-500 focus:outline-none"
                value={formState.description}
                onChange={(event) => onInputChange('description', event.target.value)}
                placeholder="Sintesi copy per il team marketing"
              />
            </div>
            <div className="grid gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-200">Anteprima (thumbnail) URL</label>
                <input
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-50 focus:border-indigo-500 focus:outline-none"
                  value={formState.thumbnailUrl}
                  onChange={(event) => onInputChange('thumbnailUrl', event.target.value)}
                  placeholder="https://cdn.regai.it/hero-thumb.jpg"
                />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="block text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Upload file principale
                  <input
                    type="file"
                    accept={formState.type === 'video' ? 'video/*' : 'image/*'}
                    className="mt-2 block w-full cursor-pointer text-sm text-slate-300 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-600 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-indigo-500"
                    onChange={(event) => onInputChange('file', event.target.files?.[0] ?? null)}
                  />
                </label>
                <label className="block text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Upload thumbnail
                  <input
                    type="file"
                    accept="image/*"
                    className="mt-2 block w-full cursor-pointer text-sm text-slate-300 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-600 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-indigo-500"
                    onChange={(event) => onInputChange('thumbnailFile', event.target.files?.[0] ?? null)}
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              className="inline-flex items-center rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={loading}
            >
              {loading ? 'Salvataggio…' : isEditing ? 'Aggiorna asset' : 'Crea asset'}
            </button>
            {isEditing && (
              <button
                type="button"
                className="inline-flex items-center rounded-xl border border-slate-700 px-5 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-500"
                onClick={resetForm}
                disabled={loading}
              >
                Annulla modifica
              </button>
            )}
            {error && <span className="text-sm font-medium text-red-300">{error}</span>}
            {success && <span className="text-sm font-medium text-emerald-300">{success}</span>}
          </div>
        </form>
      </section>

      <section className="space-y-4">
        <header>
          <h3 className="text-lg font-semibold text-slate-50">Asset attivi</h3>
          <p className="text-sm text-slate-400">
            Gestisci ordinamento, sostituisci file e collega nuove creatività per le sezioni pubbliche.
          </p>
        </header>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={`rounded-full px-3 py-1 text-xs font-semibold border transition ${
              activePlacement === 'all'
                ? 'border-indigo-400/40 bg-indigo-500/20 text-indigo-100'
                : 'border-slate-700 bg-slate-800/60 text-slate-300 hover:border-slate-500'
            }`}
            onClick={() => setActivePlacement('all')}
          >
            Tutto
          </button>
          {placementPresets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`rounded-full px-3 py-1 text-xs font-semibold border transition ${
                activePlacement === preset.id
                  ? 'border-indigo-400/40 bg-indigo-500/20 text-indigo-100'
                  : 'border-slate-700 bg-slate-800/60 text-slate-300 hover:border-slate-500'
              }`}
              onClick={() => setActivePlacement(preset.id)}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {visibleAssets.map((asset) => (
            <article
              key={asset.id}
              className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/50 p-5 shadow-lg"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="rounded-full bg-indigo-500/30 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-200">
                    {asset.type}
                  </span>
                  <h4 className="mt-3 text-lg font-semibold text-slate-50">{asset.title}</h4>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-500">{asset.placement}</p>
                  {asset.description && (
                    <p className="mt-2 text-sm text-slate-300">{asset.description}</p>
                  )}
                  <div className="mt-3 text-xs text-slate-400">
                    {asset.order !== null && <span>Ordine: {asset.order}</span>}
                  </div>
                </div>
                <div className="w-40">
                  {asset.type === 'image' ? (
                    <img
                      src={asset.thumbnailUrl ?? asset.mediaUrl}
                      alt={asset.title}
                      className="h-24 w-full rounded-2xl object-cover"
                    />
                  ) : (
                    <div className="flex h-24 w-full items-center justify-center rounded-2xl border border-indigo-500/30 bg-indigo-500/10 text-indigo-200">
                      🎬
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-3 text-xs">
                {asset.mediaUrl && (
                  <a
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-3 py-1 text-indigo-200 hover:border-indigo-500 hover:text-indigo-100"
                    href={asset.mediaUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    URL media
                  </a>
                )}
                {asset.thumbnailUrl && (
                  <a
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-3 py-1 text-slate-300 hover:border-indigo-500 hover:text-indigo-100"
                    href={asset.thumbnailUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Thumbnail
                  </a>
                )}
              </div>

              <div className="mt-5 flex gap-3">
                <button
                  type="button"
                  className="inline-flex flex-1 items-center justify-center rounded-xl border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:border-indigo-500"
                  onClick={() => handleEdit(asset)}
                  disabled={loading}
                >
                  Modifica
                </button>
                <button
                  type="button"
                  className="inline-flex flex-1 items-center justify-center rounded-xl border border-red-600/40 bg-red-600/10 px-3 py-2 text-sm font-semibold text-red-200 transition hover:bg-red-600/20"
                  onClick={() => handleDelete(asset)}
                  disabled={loading}
                >
                  Elimina
                </button>
              </div>
            </article>
          ))}
          {visibleAssets.length === 0 && (
            <div className="rounded-3xl border border-dashed border-slate-800 bg-slate-900/30 p-10 text-center text-slate-400">
              Nessun asset ancora disponibile. Carica il primo contenuto per popolare le sezioni visive del sito.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
